import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { css } from '@emotion/react'
import { fonts } from 'shared-ui'
import Profile from '../../context/Profile'
import ZoneResults from '../test/ZoneResults'
import Modal from '../_layout/Modal'
import Cta from '../_control/Cta'
import MeasurementPicker from '../_control/MeasurementPicker'
import useTracker from '../../hooks/useTracker'
import { MTM_EVENT_TYPES } from '../../utils/matomo'
import { FormattedMessage } from 'react-intl'
import useRawPromise from '../../hooks/useRawPromise'

export default function TestZoneResultsEditor({
    test,
    refetch,
    backgroundType,
}) {
    const {
        id,
        testCategory,
        rawCertified,
        zoneResults,
    } = test

    const track = useTracker()

    const { put } = useRawPromise()

    const { data: profile } = useContext(Profile) || {}

    const isEditable = useMemo(() => (
        !rawCertified || profile.admin
    ), [rawCertified, profile])

    const [modifiedZoneResults, setModifiedZoneResults] = useState(zoneResults)
    useEffect(() => {
        setModifiedZoneResults(zoneResults)
    }, [zoneResults, setModifiedZoneResults])

    // selecting a zone for editing (null means modal is closed)
    const [modifiedZone, setModifiedZone] = useState(null)
    const selectZoneForModification = useCallback((zone) => {
        setModifiedZone(zone)
    }, [setModifiedZone])

    // close modal
    const closeModal = useCallback(() => {
        setModifiedZone(null)
    }, [setModifiedZone])

    // editing
    const handleChangeMeasurement = useCallback((measurement) => {
        setModifiedZone(zone => ({
            ...zone,
            measurement,
        }))
    }, [setModifiedZone])

    // saving
    const [zoneSubmitBusy, setZoneSubmitBusy] = useState()

    const doSubmit = useCallback(async () => {
        setZoneSubmitBusy(true)
        const res = await put(modifiedZone?.['@id'], { body: modifiedZone })

        if (res?.data?.['@id']) {
            const submittedZone = res.data
            const zoneIndex = modifiedZoneResults.findIndex(zone => zone.id === submittedZone.id)
            if (zoneIndex > -1) {
                const newModifiedZoneResults = [...modifiedZoneResults]
                newModifiedZoneResults[zoneIndex] = submittedZone
                setModifiedZoneResults(newModifiedZoneResults)
            }
        }

        setZoneSubmitBusy(false)
    }, [modifiedZone, modifiedZoneResults, put])

    const handleSave = useCallback(() => {
        doSubmit()
            .then(() => {
                track({ 'event': MTM_EVENT_TYPES['input-measurement'] })
                refetch()
                closeModal()
            })
    }, [modifiedZoneResults, setModifiedZoneResults, doSubmit, closeModal])

    const {
        allowedDecimals = 0,
        minRatingValue = 0,
        maxRatingValue = 100,
    } = testCategory

    return (
        <React.Fragment>
            <ZoneResults
                gridId={id}
                backgroundType={backgroundType}
                css={css`flex-grow: 1; height: 100%`}
                isPercentageTemplate={testCategory?.pitchTemplate?.templateType === 'percentage'}
                decimals={testCategory?.allowedDecimals}
                getZoneBackendColor={(zone) => (zone.rating?.assessment?.frontendCode)}
                unitSI={testCategory.unitSI}
                zoneResults={modifiedZoneResults}
                enableClicks={isEditable}
                onClickZone={selectZoneForModification}
            />
            {modifiedZone &&
                <Modal css={css`width: 64em;`} onClickClose={closeModal}>
                    <div css={css`display: flex; flex-direction: column; align-items: stretch; width: 100%; gap: 2rem; height: 100%;`}>
                        <h6 css={css`font-size: 2em; font-weight: 300; font-family: ${fonts.special};`}>
                            <FormattedMessage id={'enterAMeasurement'} />
                        </h6>
                        <MeasurementPicker
                            css={css`flex-grow: 1;`}
                            value={modifiedZone.measurement}
                            onChange={handleChangeMeasurement}
                            decimals={allowedDecimals}
                            min={minRatingValue}
                            max={maxRatingValue}
                            metricUnit={testCategory.unitSI}
                        />
                        <Cta
                            css={css`align-self: flex-end;`}
                            onClick={handleSave}
                            loading={zoneSubmitBusy}
                            signal='positive'
                        >
                            <FormattedMessage id={'save'} />
                        </Cta>
                    </div>
                </Modal>
            }
        </React.Fragment>
    )
}

